export const remoteImageSkeletonScript = `
(() => {
  if (window.__playbrainRemoteImageSkeletons) return;
  window.__playbrainRemoteImageSkeletons = true;

  const remoteImageSelector = "img";
  const remoteBackgroundSelector = "[data-remote-background-src]";
  const enhancedImages = new WeakSet();
  const enhancedBackgrounds = new WeakMap();

  const isRemoteSource = (source) => {
    if (!source || typeof source !== "string") return false;
    try {
      const url = new URL(source, window.location.href);
      return url.protocol === "http:" || url.protocol === "https:" ? url.origin !== window.location.origin : false;
    } catch {
      return /^https?:\\/\\//i.test(source);
    }
  };

  const imageSource = (image) =>
    image.currentSrc ||
    image.getAttribute("src") ||
    image.getAttribute("data-src") ||
    image.getAttribute("srcset")?.split(",")[0]?.trim()?.split(/\\s+/)[0] ||
    "";

  const markImageLoaded = (image) => {
    image.dataset.remoteImageLoaded = "true";
    delete image.dataset.remoteImageError;
  };

  const markImageError = (image) => {
    image.dataset.remoteImageError = "true";
    delete image.dataset.remoteImageLoaded;
  };

  const enhanceImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;
    if (!isRemoteSource(imageSource(image))) return;

    image.dataset.remoteImage = "true";
    if (!enhancedImages.has(image)) {
      enhancedImages.add(image);
      image.addEventListener("load", () => markImageLoaded(image));
      image.addEventListener("error", () => markImageError(image));
    }

    if (image.complete && image.naturalWidth > 0) {
      markImageLoaded(image);
      return;
    }

    if (!image.complete) {
      delete image.dataset.remoteImageLoaded;
      delete image.dataset.remoteImageError;
    }
  };

  const enhanceBackground = (element) => {
    if (!(element instanceof HTMLElement)) return;
    const source = element.dataset.remoteBackgroundSrc;
    if (!isRemoteSource(source)) return;
    if (enhancedBackgrounds.get(element) === source) return;

    enhancedBackgrounds.set(element, source);
    element.dataset.remoteBackground = "true";
    delete element.dataset.remoteBackgroundLoaded;
    delete element.dataset.remoteBackgroundError;

    const probe = new Image();
    probe.onload = () => {
      element.dataset.remoteBackgroundLoaded = "true";
      delete element.dataset.remoteBackgroundError;
    };
    probe.onerror = () => {
      element.dataset.remoteBackgroundError = "true";
      delete element.dataset.remoteBackgroundLoaded;
    };
    probe.src = source;
  };

  const scan = (root = document) => {
    if (root instanceof HTMLImageElement) enhanceImage(root);
    if (root instanceof HTMLElement) enhanceBackground(root);

    root.querySelectorAll?.(remoteImageSelector).forEach(enhanceImage);
    root.querySelectorAll?.(remoteBackgroundSelector).forEach(enhanceBackground);
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        if (mutation.target instanceof HTMLImageElement) enhanceImage(mutation.target);
        if (mutation.target instanceof HTMLElement) enhanceBackground(mutation.target);
        continue;
      }

      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element || node instanceof DocumentFragment) scan(node);
      });
    }
  });

  const start = () => {
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["src", "srcset", "data-src", "data-remote-background-src"],
      childList: true,
      subtree: true,
    });
    scan();
  };

  const startAfterHydration = () => window.setTimeout(start, 1000);

  if (document.readyState === "complete") {
    startAfterHydration();
  } else {
    window.addEventListener("load", startAfterHydration, { once: true });
  }
})();
`
